import { Injectable, OnDestroy, inject } from '@angular/core';

import { NumericConstants, StaticValues } from '@constant/static-value-constant';
import { environment } from '../../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class AutoLogoutService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private throttleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly listenerController = new AbortController();

  /**
   * Initialize the idle watcher to track user activity and manage timeout.
   * @returns
   */
  initializeIdleWatcher(): void {
    if (!environment.isAutoLogoutForIdleScreen) return;

    this.updateLastAction();
    this.setupUserActivityListeners();
    this.startIdleCheckInterval();
  }

  /**
   * Update the last action timestamp in local storage to the current time
   */
  updateLastAction(): void {
    localStorage.setItem(StaticValues.LAST_ACTION_KEY, Date.now().toString());
  }

  /**
   * Set up throttled event listeners for user activity using AbortController for cleanup
   */
  setupUserActivityListeners(): void {
    const opts = { passive: true, signal: this.listenerController.signal } as const;
    const handler = () => this.throttledUpdateLastAction();
    ['click', 'mouseout', 'mousemove', 'keydown', 'keyup', 'keypress'].forEach(event =>
      document.body.addEventListener(event, handler, opts),
    );
  }

  /*
   * Throttled wrapper around localStorage timestamp writes.
   * Prevents excessive write operations when the user generates rapid events (e.g. mouse
   * moves) by coalescing all activity within a 1-second window into a single write.
   */
  private throttledUpdateLastAction(): void {
    if (this.throttleTimer) return;
    this.throttleTimer = setTimeout(() => {
      localStorage.setItem(StaticValues.LAST_ACTION_KEY, Date.now().toString());
      this.throttleTimer = null;
    }, 1000);
  }

  /**
   * Start an interval to check for idle timeout at regular intervals
   */
  startIdleCheckInterval(): void {
    setInterval(() => {
      this.checkForIdleTimeout();
    }, NumericConstants.CHECK_INTERVAL_MS);
  }

  /**
   * Check if the user has been idle for too long and log them out if so
   */
  checkForIdleTimeout(): void {
    const idleTimeout = this.getLastAction() + environment.idleTimeOfApplication;

    if (idleTimeout - Date.now() < NumericConstants.ZERO && this.authService.isAuthenticated()) {
      this.authService.logoutUser();
    }
  }

  /**
   * Get the last action from local storage
   * @returns The timestamp of the last user action, or current time if not found
   */
  getLastAction(): number {
    const lastActionStr = localStorage.getItem(StaticValues.LAST_ACTION_KEY);
    const lastAction = Number.parseInt(lastActionStr ?? '', 10);
    return lastAction || Date.now();
  }

  /*
   * Cleans up all side-effects created by this service.
   * Aborts the shared AbortController so that all DOM event listeners registered via
   * setupUserActivityListeners are removed in a single call, and clears any pending
   * throttle timer to prevent a stale callback firing after destruction.
   */
  ngOnDestroy(): void {
    this.listenerController.abort();
    if (this.throttleTimer) clearTimeout(this.throttleTimer);
  }
}
